import { Guide } from '../../types/guide';

const guide: Guide = {
  slug: 'pdf-word-excel-converter-tips',
  title: 'Converter Tips & Tricks: Batch, Markdown, Excel Data and More',
  description:
    'Get more out of the PDF, Word and Excel converter: batch conversion, Markdown output, searching results, Excel to CSV or JSON, and sending text to the editor.',
  category: 'Converter',
  publishedDate: '2026-11-03',
  affiliateCategory: 'formats',
  body: `
The [Converter](/) turns PDF, Word (\`.docx\`) and Excel (\`.xlsx\`) files into plain text or Markdown, entirely in your browser. These tips cover the features people most often miss.

## Convert many files at once

1. Drag several files onto the upload area (or use **Browse Files** and multi-select).
2. Tick the checkbox next to each file you want in the output — or use **Select All**.
3. With more than one file selected, the output panel shows **Combined Text** (or Combined Markdown) with every file, in list order.

Use **Download Selected** to save the combined result in one go, or **Download Current File** for just the file you're viewing.

## Plain text or Markdown?

Use the **Text / Markdown** toggle above the output:

- **Text** is best for word counts, quick copying, or feeding search and analysis tools.
- **Markdown** keeps structure: headings, bold and italic, lists and tables. It's ideal for notes apps (Obsidian, Notion), wikis, README files and AI chat tools, which understand Markdown well.

In Markdown mode you can flip between **Preview** (formatted) and **Raw** (the Markdown source) to check the result before copying.

> **Good to know:** Word and Excel files contain real structure, so their Markdown is very accurate. PDFs don't store headings or tables — the converter infers them from font sizes and layout, so PDF Markdown is marked "best-effort". A quick pass in the [Text Editor](/text-editor) usually fixes anything odd.

## Search inside the output

Type in **Search in output** to highlight every match and see a count. It's a fast way to check whether a long contract or report mentions a term — without opening the file in another app.

The chips above the output also give you **words, characters, lines, estimated pages and reading time**, which is handy for essays and submissions with length limits.

## Excel files: get the data, not just the text

For \`.xlsx\` files, the download area also offers:

- **Download .csv** — the sheet's rows as comma-separated values, ready for any spreadsheet, database or script.
- **Download .json** — each row as an object keyed by the header row, ready for code.

In Markdown mode, every worksheet becomes its own heading followed by a table, which is perfect for pasting data into documentation.

## Keep working on the text

Click **Open in Text Editor** (the pen icon above the output) to send the converted text straight to the [Text Editor](/text-editor). From there you can:

- fix hard-wrapped PDF lines with **Fix PDF line breaks**,
- turn a Markdown table into CSV or JSON with **Convert format**,
- or export a clean, formatted PDF.

Your previous editor draft isn't lost — it's one **Undo** away.

## Troubleshooting

- **"No text could be extracted"** — the PDF is probably a scan (an image of text). See [How to extract text from a scanned PDF](/guides/how-to-extract-text-from-a-scanned-pdf).
- **Text comes out in a strange order** — multi-column layouts can interleave. Try the **Flatten** option on PDFs marked "Has layers", or tidy up in the editor.
- **Password-protected PDFs** need the password removed before they can be read.
`,
};

export default guide;
